import * as RadixDropdownMenu from '@radix-ui/react-dropdown-menu';
import { type KeyboardEvent, memo, type RefObject, useCallback, useEffect, useRef, useState } from 'react';

import { ChevronDownIcon, LayoutGridIcon } from '@air/design-icons';
import { usePortalStore } from '@air/primitive-portal';
import { cn } from '@air/utils-tailwind';

import { WorkspaceAgentMentionContextsKitMenuOption } from '~/components/v2/components/workspace/workspace-agent-panel/components/composer/mentions/WorkspaceAgentMentionContextsKitMenuOption';
import { workspaceAgentMentionContextsSelector } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentComposerPopoverChrome';
import { type WorkspaceAgentBrandKitOption } from '~/components/v2/components/workspace/workspace-agent-panel/mentions/types';
import {
  WORKSPACE_AGENT_MENTION_CONTEXTS_KIT_MENU_ATTRIBUTE,
  type WorkspaceAgentMentionDismissReason,
} from '~/components/v2/components/workspace/workspace-agent-panel/mentions/workspaceAgentMentionConstants';
import { preserveWorkspaceAgentComposerFocus } from '~/components/v2/components/workspace/workspace-agent-panel/utils/preserveWorkspaceAgentComposerFocus';

const CHOOSE_BRAND_KIT_LABEL = 'Choose brand kit';
const CHOOSE_BRAND_KIT_LABEL_ID = 'workspace-agent-mention-choose-brand-kit-label';

export type WorkspaceAgentMentionContextsKitDropdownProps = {
  brandKits: readonly WorkspaceAgentBrandKitOption[];
  onDismiss: (reason: WorkspaceAgentMentionDismissReason) => void;
  onMenuOpening: () => void;
  onOpenChange: (open: boolean) => void;
  onRefocusEditor: () => void;
  onSelectBrandKit: (brandKitId: string) => void;
  /** Lifted open state for hiding mention rows — only used to force-close, not to open. */
  open: boolean;
  selectedBrandKitId: string | null;
  triggerRef: RefObject<HTMLButtonElement>;
};

export const WorkspaceAgentMentionContextsKitDropdown = memo(
  ({
    brandKits,
    onDismiss,
    onMenuOpening,
    onOpenChange,
    onRefocusEditor,
    onSelectBrandKit,
    open: parentKitMenuOpen,
    selectedBrandKitId,
    triggerRef,
  }: WorkspaceAgentMentionContextsKitDropdownProps) => {
    const portal = usePortalStore((state) => state.portal);
    const canPickContext = brandKits.length > 1;
    const selectedKit = brandKits.find((kit) => kit.id === selectedBrandKitId);
    const selectorLabel = selectedKit?.name ?? 'Contexts';
    const styles = workspaceAgentMentionContextsSelector();

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const previousParentKitMenuOpenRef = useRef(parentKitMenuOpen);

    useEffect(() => {
      const wasParentOpen = previousParentKitMenuOpenRef.current;
      previousParentKitMenuOpenRef.current = parentKitMenuOpen;

      if (wasParentOpen && !parentKitMenuOpen) {
        setIsMenuOpen(false);
      }
    }, [parentKitMenuOpen]);

    const handleCloseAutoFocus = useCallback(
      (event: Event) => {
        event.preventDefault();
        onRefocusEditor();
      },
      [onRefocusEditor],
    );

    const handleMenuOpenChange = useCallback(
      (nextOpen: boolean) => {
        setIsMenuOpen(nextOpen);

        if (nextOpen) {
          onMenuOpening();
        }

        onOpenChange(nextOpen);
      },
      [onMenuOpening, onOpenChange],
    );

    const handleTriggerKeyDown = useCallback(
      (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== 'Tab' || event.metaKey || event.ctrlKey || event.altKey) {
          return;
        }

        event.preventDefault();

        if (isMenuOpen) {
          handleMenuOpenChange(false);
        }

        if (event.shiftKey) {
          onRefocusEditor();
          onDismiss('tab');

          return;
        }

        onRefocusEditor();
      },
      [handleMenuOpenChange, isMenuOpen, onDismiss, onRefocusEditor],
    );

    const handleMenuEscapeKeyDown = useCallback((event: globalThis.KeyboardEvent) => {
      event.stopPropagation();
    }, []);

    const selectorLabelCluster = (
      <>
        <LayoutGridIcon aria-hidden className="size-4 shrink-0" />
        <span className={styles.selectorLabelCluster()}>
          <span className={styles.selectorLabelText()}>{selectorLabel}</span>
          {canPickContext ? (
            <ChevronDownIcon
              aria-hidden
              className={cn(styles.chevron(), 'transition-none', isMenuOpen && 'rotate-180')}
            />
          ) : null}
        </span>
      </>
    );

    if (!canPickContext) {
      return (
        <div className={styles.stickyButton()} data-testid="WORKSPACE_AGENT_MENTION_CONTEXTS_SELECTOR">
          {selectorLabelCluster}
        </div>
      );
    }

    return (
      <RadixDropdownMenu.Root dir="ltr" modal={false} onOpenChange={handleMenuOpenChange} open={isMenuOpen}>
        <RadixDropdownMenu.Trigger asChild>
          <button
            aria-label={`${CHOOSE_BRAND_KIT_LABEL}, ${selectorLabel}`}
            className={cn(
              styles.stickyButton(),
              'outline-none ads:hover:bg-hover-subtle hover:bg-gray-4 ads:data-[state=open]:bg-hover-subtle data-[state=open]:bg-gray-4',
            )}
            data-testid="WORKSPACE_AGENT_MENTION_CONTEXTS_SELECTOR"
            onKeyDown={handleTriggerKeyDown}
            onMouseDown={preserveWorkspaceAgentComposerFocus}
            ref={triggerRef}
            tabIndex={-1}
            type="button"
          >
            {selectorLabelCluster}
          </button>
        </RadixDropdownMenu.Trigger>
        <RadixDropdownMenu.Portal container={portal}>
          <RadixDropdownMenu.Content
            align="start"
            className={styles.kitMenuContent()}
            onCloseAutoFocus={handleCloseAutoFocus}
            onEscapeKeyDown={handleMenuEscapeKeyDown}
            side="bottom"
            sideOffset={0}
            {...{ [WORKSPACE_AGENT_MENTION_CONTEXTS_KIT_MENU_ATTRIBUTE]: '' }}
          >
            <RadixDropdownMenu.Label className="sr-only" id={CHOOSE_BRAND_KIT_LABEL_ID}>
              {CHOOSE_BRAND_KIT_LABEL}
            </RadixDropdownMenu.Label>
            <RadixDropdownMenu.RadioGroup
              aria-labelledby={CHOOSE_BRAND_KIT_LABEL_ID}
              className="flex flex-col gap-0 py-1"
              onValueChange={onSelectBrandKit}
              value={selectedBrandKitId ?? undefined}
            >
              {brandKits.map((kit) => (
                <WorkspaceAgentMentionContextsKitMenuOption key={kit.id} kit={kit} />
              ))}
            </RadixDropdownMenu.RadioGroup>
          </RadixDropdownMenu.Content>
        </RadixDropdownMenu.Portal>
      </RadixDropdownMenu.Root>
    );
  },
);

WorkspaceAgentMentionContextsKitDropdown.displayName = 'WorkspaceAgentMentionContextsKitDropdown';
